import type { Category, ContentKind, SearchHit } from '@viva/core';
import { isVivaError, MIN_QUERY_CHARS, prepareQuery } from '@viva/core';
import { LazyImage } from '@viva/ui';
import { useOptionalAccount } from '../account/AccountGate';
import { imageUrl } from '../network';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CategoryTags } from '../category-tags';
import { categoryTags } from '../category-tags';
import { ErrorMessage } from '../components/ErrorMessage';
import { matchesFilter, TagFilterBar, useTagFilter } from '../components/TagFilters';
import { useCatalog } from '../state/catalog-context';
import { isOpenable, usePlay } from '../state/playback-context';
import { t, useLanguage } from '../i18n';
import type { MessageKey } from '../i18n/en';
import { remoteInUse } from '../spatial-navigation';

const FILTERS: { id: 'all' | ContentKind; label: MessageKey }[] = [
  { id: 'all', label: 'search.all' },
  { id: 'live', label: 'nav.live' },
  { id: 'movie', label: 'nav.movies' },
  { id: 'series', label: 'nav.series' },
];
const KIND_LABELS: Record<ContentKind, MessageKey> = { live: 'kind.live', movie: 'kind.movie', series: 'kind.series' };
const DEBOUNCE_MS = 200;
const LIMIT = 60;
/** With a language or country chosen, matches are drawn from this many and then narrowed to LIMIT. */
const FILTERED_POOL = 1000;
const KINDS: ContentKind[] = ['live', 'movie', 'series'];

interface TaggedCategory {
  sourceId: string;
  category: Category;
  tags: CategoryTags;
  itemCount: number;
}

/** A search is reported once the viewer has stopped typing for this long. */
const SEARCH_REPORT_AFTER_MS = 1500;

const categoryId = (sourceId: string, kind: ContentKind, key: string) => `${sourceId}\u0000${kind}\u0000${key}`;

export function SearchScreen() {
  useLanguage();
  const { catalog, sources } = useCatalog();
  const play = usePlay();
  const [text, setText] = useState('');
  const [filter, setFilter] = useState<'all' | ContentKind>('all');
  const [result, setResult] = useState<{ hits: SearchHit[]; ms: number; query: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const tooShort = prepareQuery(text) === null;

  // What people search for (admin statistics): once they stop typing for a moment.
  // A ref, so a new account object (session refresh) doesn't report the same search twice.
  const account = useOptionalAccount();
  const reportSearch = useRef(account?.reportSearch);
  reportSearch.current = account?.reportSearch;
  useEffect(() => {
    if (tooShort) return undefined;
    const timer = setTimeout(() => reportSearch.current?.(text.trim()), SEARCH_REPORT_AFTER_MS);
    return () => clearTimeout(timer);
  }, [text, tooShort]);

  // Every category of every source, tagged with its language and country.
  const [tagged, setTagged] = useState<TaggedCategory[]>([]);
  useEffect(() => {
    let active = true;
    const jobs = (sources ?? []).flatMap((source) =>
      KINDS.map((kind) =>
        catalog.listCategories(source.id, kind).then(
          (list) => list.map((category) => ({ sourceId: source.id, category, tags: categoryTags(category.name), itemCount: category.itemCount })),
          () => [],
        ),
      ),
    );
    void Promise.all(jobs).then((lists) => active && setTagged(lists.flat()));
    return () => {
      active = false;
    };
  }, [catalog, sources]);
  const inKind = useMemo(() => (filter === 'all' ? tagged : tagged.filter(({ category }) => category.kind === filter)), [tagged, filter]);
  const tagFilter = useTagFilter('search', inKind);
  const { active: place, filtered } = tagFilter;
  // Categories the chosen language and country allow, or null for all.
  const allowed = useMemo(
    () => (filtered ? new Set(inKind.filter(({ tags }) => matchesFilter(tags, place)).map(({ sourceId, category }) => categoryId(sourceId, category.kind, category.key))) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `place` is rebuilt each render from these values
    [inKind, filtered, place.language, place.country],
  );

  useEffect(() => {
    if (tooShort) {
      setResult(null);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      const started = performance.now();
      catalog.search({ query: text, limit: allowed ? FILTERED_POOL : LIMIT, kinds: filter === 'all' ? undefined : [filter] }).then(
        (found) => {
          if (!active) return;
          const hits = allowed ? found.filter(({ item }) => allowed.has(categoryId(item.sourceId, item.kind, item.categoryKey))).slice(0, LIMIT) : found;
          setError(null);
          setResult({ hits, ms: Math.round(performance.now() - started), query: text });
        },
        (cause: unknown) => active && setError(cause),
      );
    }, DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [catalog, text, filter, tooShort, allowed]);

  const sourceNames = new Map(sources?.map((source) => [source.id, source.name]));
  const showSource = (sources?.length ?? 0) > 1;

  return (
    <section className="screen screen--search">
      <header className="screen__header">
        <h1>{t('nav.search')}</h1>
      </header>
      <input className="search-input" type="search" value={text} onChange={(event) => setText(event.target.value)} placeholder={t('search.placeholder')} aria-label={t('nav.search')}
        // Typing straight away with a keyboard; with a TV remote the box is highlighted and OK opens the keyboard.
        autoFocus={!remoteInUse()} data-autofocus />
      <div className="segmented" role="tablist">
        {FILTERS.map((option) => (
          <button key={option.id} type="button" role="tab" aria-selected={filter === option.id} className="segmented__item" onClick={() => setFilter(option.id)}>
            {t(option.label)}
          </button>
        ))}
      </div>
      <TagFilterBar state={tagFilter} />
      {error && isVivaError(error) ? <ErrorMessage error={error} /> : null}
      {tooShort ? <p className="muted">{t('search.tooShort', { count: MIN_QUERY_CHARS })}</p> : null}
      {result && !tooShort ? (
        <>
          <p className="muted small">
            {result.hits.length === 0 ? t('search.none') : t('search.results', { count: `${result.hits.length}${result.hits.length === LIMIT ? '+' : ''}` })} · {result.ms} ms
          </p>
          <ul className="search-results">
            {result.hits.map(({ item }) => (
              <li key={`${item.sourceId}:${item.key}`}>
                <button type="button" className="search-result" disabled={!isOpenable(item)} onClick={() => play({ item })}>
                  <LazyImage className="search-result__image" src={imageUrl(item.logo)} label={item.name} />
                  <span className="search-result__name">{item.name}</span>
                  <span className="badge">{t(KIND_LABELS[item.kind])}</span>
                  {showSource ? <span className="muted small">{sourceNames.get(item.sourceId)}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
